import { format, isValid, parseISO } from 'date-fns'

import { SheetSectionCard } from '@/components/SheetSectionCard'
import { Skeleton } from '@/components/ui/skeleton'
import type {
  ChronicConditionRecord,
  LabResultRecord,
} from '@/features/clinical-data/types'
import type { ClientClinical } from '@/features/patients/hooks/useClientClinical'
import type {
  ClientHistory,
  HistorySlice,
} from '@/features/patients/hooks/useClientHistory'
import { SHEET_TABLE_CELL, SHEET_TABLE_HEAD } from './sheetTableStyles'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { ClientVisitMix } from './ClientVisitMix'

function formatDate(value: string, pattern: string): string {
  const date = parseISO(value)
  return isValid(date) ? format(date, pattern) : value
}

interface Column<T> {
  label: string
  cell: (record: T) => React.ReactNode
  className?: string
}

const CONDITION_COLUMNS: Column<ChronicConditionRecord>[] = [
  {
    label: 'Condition',
    cell: (c) => c.condition_description,
    className: 'whitespace-normal',
  },
  {
    label: 'Code',
    cell: (c) => c.condition_code,
    className: 'font-mono text-[13px]',
  },
  {
    label: 'Since',
    cell: (c) => formatDate(c.effective_date, 'MMM yyyy'),
    className: 'whitespace-nowrap',
  },
]

const LAB_COLUMNS: Column<LabResultRecord>[] = [
  {
    label: 'Test',
    cell: (l) => l.test_name ?? l.test_code ?? '—',
    className: 'whitespace-normal',
  },
  {
    label: 'Result',
    cell: (l) => l.result_raw,
    className: 'font-mono text-[13px] whitespace-normal',
  },
  {
    label: 'Observed',
    cell: (l) => formatDate(l.observed_at, 'MMM d, yyyy'),
    className: 'whitespace-nowrap',
  },
]

function ClinicalTable<T extends { id: string }>({
  title,
  slice,
  columns,
  empty,
}: {
  title: string
  slice: HistorySlice<T>
  columns: Column<T>[]
  empty: string
}) {
  if (!slice.allowed) return null
  const count =
    slice.isLoading || slice.isError
      ? ''
      : ` · ${slice.rows.length}${slice.truncated ? '+' : ''}`

  return (
    <SheetSectionCard
      title={`${title}${count}`}
      body={
        slice.isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : slice.isError || slice.rows.length === 0 ? (
          <p className="text-muted-foreground py-2 text-center text-sm">
            {slice.isError ? `Could not load ${title.toLowerCase()}` : empty}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {columns.map((column) => (
                  <TableHead key={column.label} className={SHEET_TABLE_HEAD}>
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {slice.rows.map((record) => (
                <TableRow key={record.id}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.label}
                      className={cn(SHEET_TABLE_CELL, column.className)}>
                      {column.cell(record)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )
      }
    />
  )
}

/**
 * What care the client receives (visit mix, from their visit history) and
 * their clinical record (conditions and labs, for viewers granted them).
 */
export function ClientClinicalTab({
  history,
  clinical,
}: {
  history: ClientHistory
  clinical: ClientClinical
}) {
  return (
    <div className="flex flex-col gap-4">
      <ClientVisitMix visits={history.visits} />
      <ClinicalTable
        title="Chronic conditions"
        slice={clinical.conditions}
        empty="No chronic conditions on file"
        columns={CONDITION_COLUMNS}
      />
      <ClinicalTable
        title="Recent labs"
        slice={clinical.labs}
        empty="No lab results on file"
        columns={LAB_COLUMNS}
      />
    </div>
  )
}
